import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/shared/components/ui/button";

interface SubscriptionsPaginationProps {
  readonly page: number;
  readonly limit: number;
  readonly totalCount: number;
  readonly onPageChange: (page: number) => void;
}

export function SubscriptionsPagination({
  page,
  limit,
  totalCount,
  onPageChange,
}: SubscriptionsPaginationProps) {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));
  const from = totalCount === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, totalCount);

  return (
    <div data-testid="subscriptions-pagination" className="flex items-center justify-between gap-3">
      <p className="font-sans text-sm font-normal text-muted-foreground">
        {t("subscriptions.list.pagination.summary", { from, to, total: totalCount })}
      </p>
      <div className="flex items-center gap-2">
        <Button
          data-testid="subscriptions-pagination-prev"
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label={t("subscriptions.list.pagination.previous")}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="font-sans text-sm font-medium tabular-nums text-foreground">
          {t("subscriptions.list.pagination.pageOf", { page, total: totalPages })}
        </span>
        <Button
          data-testid="subscriptions-pagination-next"
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          aria-label={t("subscriptions.list.pagination.next")}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
